import React, { useState, useCallback } from 'react'
import type { SongSettings } from '../../../../types/song'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { SectionDivider } from '../../shared/SectionDivider'
import { GradientBanner } from '../../shared/GradientBanner'

interface StyleTabProps {
  settings: SongSettings
  onUpdateSettings: <K extends keyof SongSettings>(key: K, value: SongSettings[K]) => void
}

/**
 * Style tab for song settings
 *
 * Merges keywords, style guide, and target audience/targets.
 * Maps to SongSettings.keyword_settings, style_guide, target_duration_minutes,
 * overall_mood, and energy_level.
 */
export const StyleTab: React.FC<StyleTabProps> = ({
  settings,
  onUpdateSettings
}) => {
  // Local state for input fields
  const [newKeyword, setNewKeyword] = useState('')
  const [newMetaphor, setNewMetaphor] = useState('')
  const [newAvoidWord, setNewAvoidWord] = useState('')
  const [newGenre, setNewGenre] = useState('')
  const [newArtist, setNewArtist] = useState('')

  // Keyword handlers
  const addKeyword = useCallback(() => {
    if (newKeyword.trim() && !settings.keyword_settings.primary_keywords.includes(newKeyword.trim())) {
      onUpdateSettings('keyword_settings', {
        ...settings.keyword_settings,
        primary_keywords: [...settings.keyword_settings.primary_keywords, newKeyword.trim()]
      })
      setNewKeyword('')
    }
  }, [newKeyword, settings.keyword_settings, onUpdateSettings])

  const removeKeyword = useCallback((keyword: string) => {
    onUpdateSettings('keyword_settings', {
      ...settings.keyword_settings,
      primary_keywords: settings.keyword_settings.primary_keywords.filter(k => k !== keyword)
    })
  }, [settings.keyword_settings, onUpdateSettings])

  const addMetaphor = useCallback(() => {
    if (newMetaphor.trim() && !settings.keyword_settings.metaphor_themes.includes(newMetaphor.trim())) {
      onUpdateSettings('keyword_settings', {
        ...settings.keyword_settings,
        metaphor_themes: [...settings.keyword_settings.metaphor_themes, newMetaphor.trim()]
      })
      setNewMetaphor('')
    }
  }, [newMetaphor, settings.keyword_settings, onUpdateSettings])

  const removeMetaphor = useCallback((metaphor: string) => {
    onUpdateSettings('keyword_settings', {
      ...settings.keyword_settings,
      metaphor_themes: settings.keyword_settings.metaphor_themes.filter(m => m !== metaphor)
    })
  }, [settings.keyword_settings, onUpdateSettings])

  const addAvoidWord = useCallback(() => {
    if (newAvoidWord.trim() && !settings.keyword_settings.avoid_words.includes(newAvoidWord.trim())) {
      onUpdateSettings('keyword_settings', {
        ...settings.keyword_settings,
        avoid_words: [...settings.keyword_settings.avoid_words, newAvoidWord.trim()]
      })
      setNewAvoidWord('')
    }
  }, [newAvoidWord, settings.keyword_settings, onUpdateSettings])

  const removeAvoidWord = useCallback((word: string) => {
    onUpdateSettings('keyword_settings', {
      ...settings.keyword_settings,
      avoid_words: settings.keyword_settings.avoid_words.filter(w => w !== word)
    })
  }, [settings.keyword_settings, onUpdateSettings])

  // Style guide handlers
  const addGenre = useCallback(() => {
    if (newGenre.trim() && !settings.style_guide.sub_genres.includes(newGenre.trim())) {
      onUpdateSettings('style_guide', {
        ...settings.style_guide,
        sub_genres: [...settings.style_guide.sub_genres, newGenre.trim()]
      })
      setNewGenre('')
    }
  }, [newGenre, settings.style_guide, onUpdateSettings])

  const removeGenre = useCallback((genre: string) => {
    onUpdateSettings('style_guide', {
      ...settings.style_guide,
      sub_genres: settings.style_guide.sub_genres.filter(g => g !== genre)
    })
  }, [settings.style_guide, onUpdateSettings])

  const addArtist = useCallback(() => {
    if (newArtist.trim() && !settings.style_guide.artist_references.includes(newArtist.trim())) {
      onUpdateSettings('style_guide', {
        ...settings.style_guide,
        artist_references: [...settings.style_guide.artist_references, newArtist.trim()]
      })
      setNewArtist('')
    }
  }, [newArtist, settings.style_guide, onUpdateSettings])

  const removeArtist = useCallback((artist: string) => {
    onUpdateSettings('style_guide', {
      ...settings.style_guide,
      artist_references: settings.style_guide.artist_references.filter(a => a !== artist)
    })
  }, [settings.style_guide, onUpdateSettings])

  return (
    <div className="space-y-6">
      <GradientBanner
        title="Style & Identity"
        description="Define keywords, artistic influences, and the overall character of your song."
        icon="🎨"
        variant="info"
      />

      {/* Keywords Section */}
      <div>
        <h3 className="text-heading font-medium text-gray-900 mb-3">Keywords</h3>

        {/* Primary Keywords */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Keywords
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
              placeholder="Add keyword..."
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
            />
            <button
              onClick={addKeyword}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.keyword_settings.primary_keywords.map((keyword) => (
              <span
                key={keyword}
                className="tag-chip inline-flex items-center px-3 py-1 rounded-full text-sm"
              >
                {keyword}
                <button
                  onClick={() => removeKeyword(keyword)}
                  className="ml-2"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Metaphor Themes */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Metaphor Themes
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newMetaphor}
              onChange={(e) => setNewMetaphor(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addMetaphor()}
              placeholder="Add metaphor theme..."
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
            />
            <button
              onClick={addMetaphor}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.keyword_settings.metaphor_themes.map((metaphor) => (
              <span
                key={metaphor}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-50 text-green-700 border border-green-200"
              >
                {metaphor}
                <button
                  onClick={() => removeMetaphor(metaphor)}
                  className="ml-2 text-green-600 hover:text-green-800"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Avoid Words */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Words to Avoid
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newAvoidWord}
              onChange={(e) => setNewAvoidWord(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addAvoidWord()}
              placeholder="Add word to avoid..."
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
            />
            <button
              onClick={addAvoidWord}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.keyword_settings.avoid_words.map((word) => (
              <span
                key={word}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-50 text-red-700 border border-red-200"
              >
                {word}
                <button
                  onClick={() => removeAvoidWord(word)}
                  className="ml-2 text-red-600 hover:text-red-800"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <SectionDivider label="Style Guide" />

      {/* Style Guide Section */}
      <div>
        <h3 className="text-heading font-medium text-gray-900 mb-3">Musical Style</h3>

        {/* Primary Genre */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Primary Genre
          </label>
          <input
            type="text"
            value={settings.style_guide.primary_genre || ''}
            onChange={(e) => onUpdateSettings('style_guide', {
              ...settings.style_guide,
              primary_genre: e.target.value || undefined
            })}
            placeholder="e.g., Pop, Rock, Country, etc."
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
          />
        </div>

        {/* Sub-genres */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sub-genres
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newGenre}
              onChange={(e) => setNewGenre(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addGenre()}
              placeholder="Add sub-genre..."
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
            />
            <button
              onClick={addGenre}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.style_guide.sub_genres.map((genre) => (
              <span
                key={genre}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-50 text-purple-700 border border-purple-200"
              >
                {genre}
                <button
                  onClick={() => removeGenre(genre)}
                  className="ml-2 text-purple-600 hover:text-purple-800"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Artist References */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Artist References
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newArtist}
              onChange={(e) => setNewArtist(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addArtist()}
              placeholder="Add artist to emulate..."
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
            />
            <button
              onClick={addArtist}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.style_guide.artist_references.map((artist) => (
              <span
                key={artist}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-50 text-indigo-700 border border-indigo-200"
              >
                {artist}
                <button
                  onClick={() => removeArtist(artist)}
                  className="ml-2 text-indigo-600 hover:text-indigo-800"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Innovation Level */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Innovation vs. Tradition: {settings.style_guide.innovation_level}
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={settings.style_guide.innovation_level}
            onChange={(e) => onUpdateSettings('style_guide', {
              ...settings.style_guide,
              innovation_level: parseInt(e.target.value)
            })}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Traditional</span>
            <span>Innovative</span>
          </div>
        </div>

        {/* Avoid Cliches */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.style_guide.avoid_cliches}
              onChange={(e) => onUpdateSettings('style_guide', {
                ...settings.style_guide,
                avoid_cliches: e.target.checked
              })}
              className="rounded border-gray-300 text-blue-600 mr-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Avoid common genre cliches</span>
          </label>
        </div>
      </div>

      <SectionDivider label="Target Audience" />

      {/* Global Targets Section */}
      <div>
        <h3 className="text-heading font-medium text-gray-900 mb-3">Target Specifications</h3>

        {/* Target Duration */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target Duration (minutes)
          </label>
          <input
            type="number"
            value={settings.target_duration_minutes || ''}
            onChange={(e) => onUpdateSettings('target_duration_minutes', e.target.value ? parseFloat(e.target.value) : undefined)}
            placeholder="e.g., 3.5"
            min="0.5"
            max="15"
            step="0.1"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
          />
        </div>

        {/* Overall Mood */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Overall Mood
          </label>
          <input
            type="text"
            value={settings.overall_mood || ''}
            onChange={(e) => onUpdateSettings('overall_mood', e.target.value || undefined)}
            placeholder="e.g., melancholic, upbeat, contemplative"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
          />
        </div>

        {/* Energy Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Energy Level: {settings.energy_level}
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={settings.energy_level}
            onChange={(e) => onUpdateSettings('energy_level', parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Calm</span>
            <span>Intense</span>
          </div>
        </div>
      </div>
    </div>
  )
}
